import { ColStyled } from './styled';
import { coverageActions } from 'core/Coverage';
import isEmpty from 'lodash/isEmpty';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { validations } from 'shared/validations';
import { yesOrNoOptions } from 'shared/fixtures';
import {
    ButtonFooter,
    ExpandButton,
    FormGroup,
    Input,
    OptionGroup,
    TagList
} from 'mol-ui';
import {
    Col,
    Row
} from 'reactstrap';
import {
    ConfirmationModal,
    ErrorMessage,
    FormLayout
} from 'views/components';
import React, { useEffect, useState } from 'react';
import {
    clearObject,
    validationSchema
} from 'shared/utils';
import {
    useDispatch,
    useErrors,
    useInput,
    useObjectState,
    useSelector
} from 'hooks';

const DEFAULT_COVERAGE = {
    affiliate_validation_method: 'freepass',
    api: '',
    business_name: '',
    cuit: '',
    id: undefined,
    name: '',
    plans: [],
    rnos_code: '',
    security_code: false
};

const CoverageRegistration = () => {
    
    const dispatch = useDispatch();
    const { id, mode } = useParams();
    const { t } = useTranslation();
    const {
        errors: coverageErrors,
        loading,
        selected: selectedCoverage
    } = useSelector('coverage');
    const { clearError, errors, setErrors } = useErrors();
    const [planInput, planValue, setPlanValue] = useInput('');
    const [form, setForm, setFormValue] = useObjectState({ ...DEFAULT_COVERAGE });
    const [modalOpen, setModalOpen] = useState(false);
    
    const validationOptions = [{
        label: t('Libre'),
        value: 'freepass'
    }, {
        label: t('API'),
        value: 'api'
    }, {
        label: t('Datos importados'),
        value: 'imported'
    }];

    useEffect(() => {
        if (['editar', 'ver'].includes(mode)) {
            dispatch(coverageActions.getCoverage(id));
        } else {
            dispatch(coverageActions.setCoverageSelected({}));
        }
    }, []);

    useEffect(() => {
        const { cuit, plans = [] } = selectedCoverage;
        setForm({
            ...DEFAULT_COVERAGE,
            ...selectedCoverage,
            cuit: cuit ? cuit.toString() : '',
            plans: plans.map(({ name }) => name)
        });
    }, [selectedCoverage, setForm]);

    const clearCoverageError = id => {
        if (coverageErrors[id]) {
            const newErrors = { ...coverageErrors, [id]: undefined };
            dispatch(coverageActions.setCoverageErrors(clearObject(newErrors)));
        }
    };

    const handleAddPlan = () => {
        if (planValue.trim()) {
            clearError('plans');
            setFormValue('plans', form.plans.includes(planValue) ? form.plans : [...form.plans, planValue.trim()]);
            setPlanValue('');
        }
    };

    const handleChange = (id, value) => {
        clearError(id);
        if (['api', 'cuit'].includes(id)) {
            clearCoverageError(id);
        }
        setFormValue(id, value);
    };

    const handleDeletePlan = ({ label }) => {
        setFormValue('plans', form.plans.filter(plan => plan !== label));
    };

    const handleSubmit = () => {
        if (!validate()) return;
        dispatch(coverageActions.coverageRegistration(form));
    };

    const modalResponse = value => {
        if (value === 'confirm') {
            dispatch(coverageActions.coverageDelete(form.id));
        }
        setModalOpen(false);
    };

    const validate = () => {
        const { isValid, errors } = validationSchema({
            api: [form.affiliate_validation_method !== 'api' || !!form.api, t('Requerido')],
            business_name: [!!form.business_name, t('Requerido')],
            cuit: [
                [!!form.cuit, t('Requerido')],
                [validations.hasOnlyNumbers(form.cuit), t('Sólo números')],
                [form.cuit.length === 11, t('Debe contener 11 dígitos')]
            ],
            name: [!!form.name, t('Requerido')],
            plans: [!isEmpty(form.plans), t('Requerido')]
        });
        setErrors(errors);
        return isValid;
    };

    const viewMode = mode === t('ver');
    const editMode = mode === t('editar');
    const title = viewMode ? t('Vista cobertura médica') : editMode ? t('Edición cobertura médica') : t('Alta cobertura médica');

    return (
        <FormLayout
            goBackTo={ t('/coberturas/listar') }
            loading={ loading }
            mode={ mode }
            rightActionTitle={ t('Importar coberturas') }
            title={ title }
        >
            <Row>
                <Col md={{ offset: 1, size: 10 }} xl={{ offset: 2, size: 8 }} xs="12">
                    <Row>
                        <ColStyled sm="12">
                            <FormGroup label={ t('Nombre comercial') }>
                                <Input
                                    disabled={ viewMode }
                                    error={ errors.business_name }
                                    value={ form.business_name }
                                    onChange={ value => handleChange('business_name', value) }
                                />
                            </FormGroup>
                        </ColStyled>
                        <ColStyled md="6" sm="12">
                            <FormGroup label={ t('Razón Social') }>
                                <Input
                                    disabled={ viewMode }
                                    error={ errors.name }
                                    value={ form.name }
                                    onChange={ value => handleChange('name', value) }
                                />
                            </FormGroup>
                        </ColStyled>
                        <ColStyled md="6" sm="12">
                            <FormGroup label="CUIT / CUIL">
                                <Input
                                    disabled={ viewMode }
                                    error={ errors?.cuit ?? coverageErrors?.cuit ?? '' }
                                    value={ form.cuit ? form.cuit.toString() : '' }
                                    onChange={ value => handleChange('cuit', value) }
                                />
                            </FormGroup>
                        </ColStyled>
                        <ColStyled md="6" sm="12">
                            <FormGroup label={ t('Código de integración') }>
                                <Input
                                    disabled={ viewMode }
                                    value={ form.rnos_code ? form.rnos_code.toString() : '' }
                                    onChange={ value => handleChange('rnos_code', value) }
                                />
                            </FormGroup>
                        </ColStyled>
                        <ColStyled xs="12">
                            <FormGroup label={ t('Validación') } margin="0px 0px 10px 0px">
                                <OptionGroup
                                    disabled={ viewMode }
                                    options={ validationOptions }
                                    value={ form.affiliate_validation_method }
                                    onChange={ value => handleChange('affiliate_validation_method', value) }
                                />
                            </FormGroup>
                        </ColStyled>
                        {
                            form.affiliate_validation_method === 'api' &&
                            <>
                                <ColStyled sm="6" xs="12">
                                    <FormGroup label="API" margin="0px 0px 10px 0px">
                                        <Input
                                            disabled={ viewMode }
                                            error={ errors?.api ?? coverageErrors?.api ?? '' }
                                            value={ form.api }
                                            onChange={ value => handleChange('api', value) }
                                        />
                                    </FormGroup>
                                </ColStyled>
                                <ColStyled sm="6" xs="12">
                                    <FormGroup label={ t('¿Requiere código de seguridad?') } margin="0px 0px 10px 0px">
                                        <OptionGroup
                                            disabled={ viewMode }
                                            options={ yesOrNoOptions }
                                            value={ !!form.security_code }
                                            onChange={ value => handleChange('security_code', value) }
                                        />
                                    </FormGroup>
                                </ColStyled>
                            </>
                        }
                        <ColStyled sm="12">
                            {
                                !viewMode &&
                                <FormGroup label={ t('Planes') }>
                                    <Row>
                                        <ColStyled xs="8">
                                            <Input
                                                { ...planInput }
                                                disabled={ viewMode }
                                                error={ !!errors.plans }
                                                placeholder={ t('Ingrese un plan y presione enter') }
                                                onEnterPress={ handleAddPlan }
                                            />
                                        </ColStyled>
                                        <Col>
                                            <ExpandButton
                                                circle
                                                disabled={ !planValue.trim() }
                                                fontSize={ 25 }
                                                icon="add"
                                                size={ 40 }
                                                onClick={ handleAddPlan }
                                            />
                                        </Col>
                                    </Row>
                                </FormGroup>
                            }
                            <TagList
                                items={ form.plans.map(label => ({ label })) }
                                readOnly={ viewMode }
                                onRemove={ handleDeletePlan }
                            />
                            <ErrorMessage
                                relative
                                error={ errors.plans }
                                marginLeft={ 0 }
                            />
                        </ColStyled>
                        <ButtonFooter margin="20px 0px 0px 0px">
                            { !viewMode && !editMode && <ExpandButton caption={ t('Confirmar') } size={ 120 } onClick={ handleSubmit } /> }
                            { editMode && <ExpandButton danger caption={ t('Eliminar') } size={ 120 } onClick={ () => setModalOpen(true) } /> }
                            { editMode && <ExpandButton caption={ t('Confirmar') } size={ 120 } onClick={ handleSubmit } /> }
                        </ButtonFooter>
                    </Row>
                </Col>
            </Row>
            <ConfirmationModal
                actionHandler={ modalResponse }
                active={ modalOpen }
                title={ t('¿Querés dar de baja esta cobertura?') }
            />
        </FormLayout>
    );
};

export default CoverageRegistration;
